import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, type DepartmentRef, type UserRef } from '../lib/api';
import { enumLabel, useI18n } from '../lib/i18n';
import { P, useAuth } from '../lib/auth';
import { refusalText } from '../lib/reach';
import { FormPage } from '../components/FormPage';
import { Field, Select, Textarea, useToast } from '../components/ui';

/**
 * Every ticket action that asks for input is its own page: assign, reassign,
 * reroute, resolve, reopen and a manual status change.
 *
 * Route: `/tickets/:id/:action`.
 */
type Action = 'assign' | 'reassign' | 'reroute' | 'resolve' | 'reopen' | 'status';

export function TicketActionFormPage() {
  const { id = '', action = 'assign' } = useParams<{ id: string; action: Action }>();
  const act = action as Action;
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [assigneeUserId, setAssignee] = useState('');
  const [targetDepartmentId, setTarget] = useState('');
  const [status, setStatus] = useState('');
  const [text, setText] = useState('');

  const ticket = useQuery({
    queryKey: ['ticket', id],
    queryFn: () => api.get<any>(`/tickets/${id}`),
  });

  const { data: users } = useQuery({
    queryKey: ['users', 'options', ticket.data?.targetDepartmentId],
    queryFn: () => api.get<UserRef[]>('/users/options', { departmentId: ticket.data.targetDepartmentId }),
    enabled: !!ticket.data && (act === 'assign' || act === 'reassign'),
  });
  // Any switched-on department: a ticket goes wherever it belongs. `GET /departments`
  // is the departments page's, and lists only the caller's own (2026-10-07).
  const { data: departments } = useQuery({
    queryKey: ['departments', 'picker'],
    queryFn: () => api.get<DepartmentRef[]>('/departments/options'),
    enabled: act === 'reroute',
  });

  const titles: Record<Action, string> = {
    assign: t('tickets.assign'),
    reassign: t('tickets.reassign'),
    reroute: t('tickets.reroute'),
    resolve: t('tickets.resolve'),
    reopen: t('tickets.reopen'),
    status: t('common.status'),
  };

  const bodyFor = (): Record<string, unknown> => {
    switch (act) {
      case 'assign':
      case 'reassign':
        return { assigneeUserId, note: text || undefined };
      case 'reroute':
        return { targetDepartmentId, reason: text };
      case 'resolve':
        return { resolution: text };
      case 'reopen':
        return { reason: text };
      case 'status':
        return { status, note: text || undefined };
    }
  };

  const valid = (): boolean => {
    switch (act) {
      case 'assign':
        return !!assigneeUserId;
      case 'reassign':
        return !!assigneeUserId && !!text.trim();
      case 'reroute':
        return !!targetDepartmentId && !!text.trim();
      case 'resolve':
      case 'reopen':
        return !!text.trim();
      case 'status':
        return !!status;
    }
  };

  const run = useMutation({
    mutationFn: () => api.post(`/tickets/${id}/${act}`, bodyFor()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });
      navigate(`/tickets/${id}`);
    },
    onError: (e: any) => toast.error(refusalText(e, t)),
  });

  // §43.2 as the server applies it to this person: the moves the status allows,
  // and only for the assignee, a team leader or an admin — whoever raised the
  // ticket may still cancel it until it is started.
  const options: string[] = ticket.data?.can?.statuses ?? [];
  // Reassigning to whoever already has it would change nothing.
  const assignees = (users ?? []).filter((u) => act !== 'reassign' || u.id !== ticket.data?.assignedUserId);

  return (
    <FormPage
      title={titles[act] ?? act}
      subtitle={ticket.data ? `${ticket.data.number} — ${ticket.data.subject}` : undefined}
      backTo={`/tickets/${id}`}
      backLabel={ticket.data?.number ?? t('nav.tickets')}
      submitLabel={t('common.confirm')}
      submitting={run.isPending}
      disabled={!valid()}
      loading={ticket.isLoading}
      error={ticket.error}
      onRetry={() => void ticket.refetch()}
      onSubmit={() => run.mutate()}
    >
      <div className="space-y-4">
        {(act === 'assign' || act === 'reassign') && (
          <Field label={t('common.assignee')} required>
            <Select value={assigneeUserId} onChange={(e) => setAssignee(e.target.value)} autoFocus>
              <option value="">—</option>
              {assignees.map((u) => (
                <option key={u.id} value={u.id}>{localized(u)}</option>
              ))}
            </Select>
            {users && users.length === 0 && !can(P.USERS_VIEW_ALL) && (
              <p className="mt-1.5 text-caption text-muted">{t('reach.nobodyToAssign')}</p>
            )}
          </Field>
        )}

        {act === 'reroute' && (
          <>
            <p className="rounded-md border border-info/30 bg-info/8 px-3 py-2 text-body-sm text-info-fg dark:text-info-dark">
              {t('tickets.rerouteNote')}
            </p>
            <Field label={t('tickets.targetDepartment')} required>
              <Select value={targetDepartmentId} onChange={(e) => setTarget(e.target.value)} autoFocus>
                <option value="">—</option>
                {(departments ?? [])
                  .filter((d) => d.id !== ticket.data?.targetDepartmentId)
                  .map((d) => (
                    <option key={d.id} value={d.id}>{localized(d)}</option>
                  ))}
              </Select>
            </Field>
          </>
        )}

        {act === 'status' && ticket.data && options.length === 0 && (
          <p className="rounded-md border border-warning/35 bg-warning/8 px-3 py-2 text-body-sm text-warning-fg dark:text-warning-dark">
            {t('tickets.statusNotYours')}
          </p>
        )}

        {act === 'status' && (
          <Field label={t('common.status')} required>
            <Select value={status} onChange={(e) => setStatus(e.target.value)} disabled={options.length === 0} autoFocus>
              <option value="">—</option>
              {options.map((s) => (
                <option key={s} value={s}>{enumLabel(s, locale)}</option>
              ))}
            </Select>
          </Field>
        )}

        <Field
          label={
            act === 'resolve'
              ? t('tickets.resolution')
              : act === 'status'
                ? t('common.notes')
                : t('common.reason')
          }
          required={act === 'reassign' || act === 'reroute' || act === 'resolve' || act === 'reopen'}
          hint={act === 'reassign' ? t('tickets.reassignNote') : act === 'assign' ? t('common.optional') : undefined}
        >
          <Textarea value={text} onChange={(e) => setText(e.target.value)} />
        </Field>
      </div>
    </FormPage>
  );
}
